import { db, storage } from '../../firebase';
import {
  collection, doc, setDoc, getDocs, updateDoc, deleteDoc, query, orderBy,
} from 'firebase/firestore';
import {
  ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject,
} from 'firebase/storage';
import { getSpecialtyCollection, getActiveSpecialty } from 'util/specialtyCollections';
import {
  validateResourceFile,
  buildResourceStoragePath,
  stripFileExtension,
} from 'util/resourceFiles';
import {
  RESOURCE_FILES_FETCH_REQUEST,
  RESOURCE_FILES_FETCH_SUCCESS,
  RESOURCE_FILES_FETCH_FAILURE,
  RESOURCE_FILE_UPLOAD_START,
  RESOURCE_FILE_UPLOAD_PROGRESS,
  RESOURCE_FILE_UPLOAD_SUCCESS,
  RESOURCE_FILE_UPLOAD_FAILURE,
  RESOURCE_FILE_UPLOADS_CLEAR,
  RESOURCE_FILE_UPDATE_REQUEST,
  RESOURCE_FILE_UPDATE_SUCCESS,
  RESOURCE_FILE_UPDATE_FAILURE,
  RESOURCE_FILE_DELETE_REQUEST,
  RESOURCE_FILE_DELETE_SUCCESS,
  RESOURCE_FILE_DELETE_FAILURE,
} from './ResourceFilesTypes';

export const clearResourceUploads = () => ({ type: RESOURCE_FILE_UPLOADS_CLEAR });

// Fetch all resource files for the active specialty
export const fetchResourceFiles = () => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: RESOURCE_FILES_FETCH_REQUEST });

      const filesRef = collection(db, getSpecialtyCollection('resourceFiles', getState));
      const snapshot = await getDocs(query(filesRef, orderBy('uploadedAt', 'desc')));
      const files = snapshot.docs.map((d) => ({ fileId: d.id, ...d.data() }));

      dispatch({ type: RESOURCE_FILES_FETCH_SUCCESS, payload: files });
      return { success: true, files };
    } catch (error) {
      dispatch({ type: RESOURCE_FILES_FETCH_FAILURE, payload: error.message });
      return { success: false, error: error.message };
    }
  };
};

// Upload one or more files. Each file is independent — one failure does not
// abort the batch. The Firestore doc is written only after the upload resolves.
export const uploadResourceFiles = (files, meta = {}) => {
  return async (dispatch, getState) => {
    const specialtyId = getActiveSpecialty(getState);
    const collectionName = getSpecialtyCollection('resourceFiles', getState);
    const uploadedByEmail = getState()?.loginAuth?.userDetails?.email || null;

    const results = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const uploadId = `${file.name}-${file.size}-${i}`;

      const validation = validateResourceFile(file);
      if (!validation.valid) {
        dispatch({ type: RESOURCE_FILE_UPLOAD_START, payload: { uploadId, fileName: file.name } });
        dispatch({ type: RESOURCE_FILE_UPLOAD_FAILURE, payload: { uploadId, error: validation.error } });
        results.push({ success: false, fileName: file.name, error: validation.error });
        continue;
      }

      // Generate the document id up front WITHOUT writing, so the storage path
      // can include it. No Firestore doc exists until the upload succeeds.
      const docRef = doc(collection(db, collectionName));
      const path = buildResourceStoragePath(specialtyId, docRef.id, file.name);

      dispatch({ type: RESOURCE_FILE_UPLOAD_START, payload: { uploadId, fileName: file.name } });

      try {
        await new Promise((resolve, reject) => {
          const task = uploadBytesResumable(storageRef(storage, path), file);
          task.on(
            'state_changed',
            (snap) => {
              const progress = snap.totalBytes
                ? Math.round((snap.bytesTransferred / snap.totalBytes) * 100)
                : 0;
              dispatch({ type: RESOURCE_FILE_UPLOAD_PROGRESS, payload: { uploadId, progress } });
            },
            (err) => reject(err),
            () => resolve()
          );
        });

        // Descriptions are per-file, aligned by index with the files array
        const description = Array.isArray(meta.descriptions)
          ? (meta.descriptions[i] || '')
          : (meta.description || '');

        await setDoc(docRef, {
          title: meta.title || stripFileExtension(file.name),
          description,
          category: meta.category || 'anki-deck',
          fileName: file.name,
          storagePath: path,
          sizeBytes: file.size,
          contentType: file.type || 'application/octet-stream',
          visibility: meta.visibility || 'private',
          version: meta.version || '',
          active: true,
          downloadCount: 0,
          uploadedAt: new Date().toISOString(),
          uploadedByEmail,
        });

        dispatch({ type: RESOURCE_FILE_UPLOAD_SUCCESS, payload: { uploadId } });
        results.push({ success: true, fileName: file.name, fileId: docRef.id });
      } catch (error) {
        dispatch({ type: RESOURCE_FILE_UPLOAD_FAILURE, payload: { uploadId, error: error.message } });
        results.push({ success: false, fileName: file.name, error: error.message });
      }
    }

    await dispatch(fetchResourceFiles());
    return results;
  };
};

// Update metadata only (title, description, category, visibility, version, active)
export const updateResourceFile = (fileId, updates) => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: RESOURCE_FILE_UPDATE_REQUEST });

      const collectionName = getSpecialtyCollection('resourceFiles', getState);
      await updateDoc(doc(db, collectionName, fileId), updates);

      dispatch({ type: RESOURCE_FILE_UPDATE_SUCCESS });
      await dispatch(fetchResourceFiles());
      return { success: true };
    } catch (error) {
      dispatch({ type: RESOURCE_FILE_UPDATE_FAILURE, payload: error.message });
      return { success: false, error: error.message };
    }
  };
};

// Swap the underlying file, keeping the same document (and therefore the same
// row in the table). The old storage object is removed after the doc points at
// the new one, so a failure never leaves the doc pointing at nothing.
export const replaceResourceFile = (fileId, file) => {
  return async (dispatch, getState) => {
    const validation = validateResourceFile(file);
    if (!validation.valid) {
      dispatch({ type: RESOURCE_FILE_UPDATE_FAILURE, payload: validation.error });
      return { success: false, error: validation.error };
    }

    try {
      dispatch({ type: RESOURCE_FILE_UPDATE_REQUEST });

      const specialtyId = getActiveSpecialty(getState);
      const collectionName = getSpecialtyCollection('resourceFiles', getState);
      const existing = getState().resourceFiles.files.find((f) => f.fileId === fileId);
      const oldPath = existing ? existing.storagePath : null;

      const newPath = buildResourceStoragePath(specialtyId, fileId, file.name);
      await uploadBytesResumable(storageRef(storage, newPath), file);

      await updateDoc(doc(db, collectionName, fileId), {
        fileName: file.name,
        storagePath: newPath,
        sizeBytes: file.size,
        contentType: file.type || 'application/octet-stream',
        uploadedAt: new Date().toISOString(),
      });

      if (oldPath && oldPath !== newPath) {
        try {
          await deleteObject(storageRef(storage, oldPath));
        } catch (err) {
          // Orphaned object is not worth failing the operation over
          console.error('Failed to remove replaced file:', err);
        }
      }

      dispatch({ type: RESOURCE_FILE_UPDATE_SUCCESS });
      await dispatch(fetchResourceFiles());
      return { success: true };
    } catch (error) {
      dispatch({ type: RESOURCE_FILE_UPDATE_FAILURE, payload: error.message });
      return { success: false, error: error.message };
    }
  };
};

// Delete the storage object first, then the doc. If storage deletion fails the
// doc survives, so the file stays visible and can be retried — better than a
// dangling row pointing at a missing object.
export const deleteResourceFile = (fileId, storagePath) => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: RESOURCE_FILE_DELETE_REQUEST });

      if (storagePath) {
        try {
          await deleteObject(storageRef(storage, storagePath));
        } catch (err) {
          if (err.code !== 'storage/object-not-found') throw err;
        }
      }

      const collectionName = getSpecialtyCollection('resourceFiles', getState);
      await deleteDoc(doc(db, collectionName, fileId));

      dispatch({ type: RESOURCE_FILE_DELETE_SUCCESS, payload: fileId });
      return { success: true };
    } catch (error) {
      dispatch({ type: RESOURCE_FILE_DELETE_FAILURE, payload: error.message });
      return { success: false, error: error.message };
    }
  };
};

// Resolve a temporary download URL for admin verification / copying
export const getResourceFileUrl = async (storagePath) => {
  return getDownloadURL(storageRef(storage, storagePath));
};
